import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldOff, Plus, X, Calendar, ArrowLeft, ArrowDownUp, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { EXPERIENCE_DATA } from '@/components/booking/bookingDefaults';
import BookingExperienceEditor from '@/components/admin/BookingExperienceEditor';
import ReorderableList from '@/components/admin/ReorderableList';

export default function AdminExperiences() {
  const { user: currentUser, isLoadingAuth: authLoading } = useAuth();
  const { toast } = useToast();
  const [showNew, setShowNew] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const queryClient = useQueryClient();

  const { data: expRecords = [], isLoading } = useQuery({
    queryKey: ['booking-experiences'],
    queryFn: () => base44.entities.BookingExperience.list('sort_order'),
    enabled: currentUser?.role === 'admin',
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['booking-experiences'] });

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
        </div>
      </div>
    );
  }

  const findRecord = (records, key) => records.find((r) => r.key === key);
  const customExperiences = expRecords.filter((r) => !EXPERIENCE_DATA[r.key]);

  // Unified, sort-ordered list of every experience (defaults + customs) for the reorder mode.
  const allExperienceItems = [
    ...Object.keys(EXPERIENCE_DATA).map((key) => {
      const rec = findRecord(expRecords, key);
      return { key, isDefault: true, record: rec, name: rec?.name || EXPERIENCE_DATA[key].name, sort_order: rec?.sort_order ?? 0 };
    }),
    ...customExperiences.map((rec) => ({ key: rec.key, isDefault: false, record: rec, name: rec.name, sort_order: rec.sort_order ?? 0 })),
  ].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  // Persist a new order: update sort_order on each record, creating a seeded
  // record (from the default) for any default experience that doesn't have one yet.
  const persistExperienceOrder = async (newOrder) => {
    setSavingOrder(true);
    try {
      await Promise.all(newOrder.map(async (it, i) => {
        if (it.record?.id) {
          await base44.entities.BookingExperience.update(it.record.id, { sort_order: i });
        } else if (it.isDefault) {
          const d = EXPERIENCE_DATA[it.key];
          await base44.entities.BookingExperience.create({
            key: it.key, name: d.name, tag: d.tag, price: d.price,
            duration_minutes: d.duration_minutes, specs: d.specs || [],
            allowed_rigs: d.allowed_rigs || [], active: true, sort_order: i,
          });
        }
      }));
      invalidate();
    } catch (e) {
      toast({ title: 'Could not save order', description: e.message, variant: 'destructive' });
    } finally {
      setSavingOrder(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <section className="py-10 md:py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-5xl mx-auto">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <p className="text-primary text-xs font-bold tracking-widest mb-2">BOOKINGS & SIM</p>
          <h1 className="text-white font-black text-3xl md:text-4xl uppercase">Experiences</h1>
          <p className="text-white/30 text-sm mt-2 max-w-xl">
            Edit existing booking experiences and pricing, or create new ones. Select which rigs each experience can use — inappropriate rigs won't appear on the booking or walk-in rig picker. Changes go live instantly.
          </p>
        </div>
      </section>

      <section className="px-6 lg:px-10 pt-10 pb-12 max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <p className="text-primary text-xs font-bold tracking-widest mb-1">BOOKING EXPERIENCES</p>
            <h2 className="text-white font-black text-xl uppercase">Experiences & Pricing</h2>
            <p className="text-white/30 text-xs mt-1">Options shown on the first booking step.</p>
          </div>
          <div className="flex items-center gap-2">
            {!showNew && !reordering && (
              <button
                onClick={() => setReordering(true)}
                className="flex items-center gap-2 border border-white/15 text-white/60 hover:text-white hover:border-white/30 font-bold text-xs tracking-wider px-4 py-2.5 transition-all"
              >
                <ArrowDownUp className="w-4 h-4" /> REORDER
              </button>
            )}
            {reordering && (
              <button
                onClick={() => setReordering(false)}
                className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider px-4 py-2.5 transition-all"
              >
                <Check className="w-4 h-4" /> DONE
              </button>
            )}
            {!showNew && !reordering && (
              <button
                onClick={() => setShowNew(true)}
                className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider px-4 py-2.5 transition-all"
              >
                <Plus className="w-4 h-4" /> NEW EXPERIENCE
              </button>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : reordering ? (
          <div>
            <p className="text-white/40 text-xs mb-3">Drag to reorder the experiences shown on the first booking step. Sorting is saved automatically as you drag.</p>
            <ReorderableList
              items={allExperienceItems}
              onReorder={persistExperienceOrder}
              getLabel={(it) => it.name}
              getBadge={(it) => <span className="text-[10px] font-bold tracking-widest px-2 py-1 shrink-0 bg-white/5 text-white/30">{it.isDefault ? 'DEFAULT' : 'CUSTOM'}</span>}
            />
            {savingOrder && <div className="flex justify-center mt-4"><Loader2 className="w-4 h-4 animate-spin text-primary" /></div>}
          </div>
        ) : (
          <div className="space-y-4">
            {showNew && (
              <div className="relative">
                <button onClick={() => setShowNew(false)} className="absolute -top-2 right-0 text-white/30 hover:text-white" aria-label="Cancel">
                  <X className="w-4 h-4" />
                </button>
                <BookingExperienceEditor isNew onCreated={() => { setShowNew(false); invalidate(); }} />
              </div>
            )}

            {Object.keys(EXPERIENCE_DATA).map((key) => (
              <BookingExperienceEditor
                key={key}
                experienceKey={key}
                defaultData={EXPERIENCE_DATA[key]}
                record={findRecord(expRecords, key)}
                onSaved={invalidate}
              />
            ))}

            {customExperiences.map((rec) => (
              <BookingExperienceEditor key={rec.id} record={rec} onSaved={invalidate} onDelete={invalidate} />
            ))}
          </div>
        )}
      </section>

      <div className="px-6 lg:px-10 pb-12 max-w-5xl mx-auto">
        <Link to="/bookings" className="inline-flex items-center gap-2 text-primary text-xs font-bold tracking-wider hover:underline">
          <Calendar className="w-4 h-4" /> VIEW BOOKINGS PAGE
        </Link>
      </div>
    </div>
  );
}